/**
 * @file TabEmailConversation.jsx
 * @description TabEmailConversation component for displaying and managing tab email conversation.
 * @component TabEmailConversation
 * @exports TabEmailConversation - Main component for displaying and managing tab email conversation.
 */

import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, TextField, Typography } from "@mui/material";
import { EMAIL_ERRORS, EMAIL_MESSAGE, EMAIL_STRING } from "../../../common/constants/agentModule/emailContants";
import { getEmailConversationById, saveEmailResponse, sendEmailResponse } from "../../../services/agentModule/email/email-conversation";
import Grid from "@mui/material/Grid2";
import { BUTTON_COLORS, SIZES, VARIANTS } from "../../../common/constants/commonConstants";
import { Save, Send } from "@mui/icons-material";
import { getUsers } from "../../../services/adminModule/users/userServices";
import RichTextEditor from "../../../components/richtextcomponent";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function TabEmailConversation({ customerguid }) {
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubjects, setEmailSubjects] = useState({});
  const [emailContents, setEmailContents] = useState({});
  const theme = useTheme();

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setIsLoading(true);
        const fetchedCustomers = await getEmailConversationById(customerguid);
        const selectedCustomer = fetchedCustomers.find(
          (cust) => cust.customerguid === customerguid
        );

        if (selectedCustomer) {
          setCustomer(selectedCustomer);
          const initialEmails = {};
          const initialSubjects = {};

          selectedCustomer.useremail.forEach((email) => {
            initialEmails[email.emailconvid] = email.cararesponse || "";
            initialSubjects[email.emailconvid] =
              email.cararesponsesubject || "";
          });

          setEmailContents(initialEmails);
          setEmailSubjects(initialSubjects);
        }
      } catch (error) {
        console.error(EMAIL_ERRORS.FETCH_EMAIL_CONVERSATION_BY_ID_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    };


    fetchCustomerData();
  }, [customerguid]);

  const handleSave = async (emailconvid) => {
    const content = emailContents[emailconvid];
    const subject = emailSubjects[emailconvid];

    if (!emailconvid || !content || !subject) return;

    try {
      setIsLoading(true);
      const userguid = localStorage.getItem("userguid");
      await saveEmailResponse({ emailconvid, content, subject, userguid });
    } catch (error) {
      console.error(EMAIL_ERRORS.SAVE_EMAIL_RESPONSE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async (emailconvid) => {
    const content = emailContents[emailconvid];
    const subject = emailSubjects[emailconvid];

    if (!emailconvid || !content || !subject) return;

    try {
      setIsLoading(true);
      const userguid = localStorage.getItem("userguid");
      await sendEmailResponse({ emailconvid, content, subject, userguid });
    } catch (error) {
      console.error(EMAIL_ERRORS.SEND_EMAIL_RESPONSE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

 

 

  return (
    <Box
      sx={{
        pr: 2,
        overflowY: "auto",
      }}
    >
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : customer ? (
        customer && customer.useremail?.length > 0 ? (
            customer.useremail.map((email, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{email.userresponsesubject}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#FAFAFA" }}>
                  <Box className="ai-chatbox">
                    
                    <Box
                      className="ai-chatbox__message ai-chatbox__message--response"
                      sx={{
                        borderColor:
                          email.cararesponseapproved === 0
                            ? theme.palette.warning.main
                            : theme.palette.primary.main + "30",
                      }}
                    >
                      <Box className="ai-chatbox__message-content">
                        {/* Editable CARA Response Subject */}
                        <Grid
                          container
                          spacing={2}
                          disableGutters
                          sx={{ my: 1, alignItems: "center" }}
                        >
                          <Grid size={SIZES.GROW}>
                            <TextField
                              fullWidth
                              label={EMAIL_STRING.SUBJECT}
                              value={emailSubjects[email.emailconvid] || ""}
                              size={SIZES.SMALL}
                              onChange={(e) =>
                                setEmailSubjects((prev) => ({
                                  ...prev,
                                  [email.emailconvid]: e.target.value,
                                }))
                              }
                              inputProps={{
                                readOnly: email.cararesponseapproved === 1,
                              }}
                            />
                          </Grid>
                          <Grid size={SIZES.AUTO}>
                            <Button
                              variant={VARIANTS.OUTLINED}
                              color={BUTTON_COLORS.PRIMARY}
                              onClick={() => handleSave(email.emailconvid)}
                              disabled={email.cararesponseapproved === 1}
                              startIcon={<Save />}
                              sx={{ minWidth: "100px" }}
                            >
                              {EMAIL_STRING.SAVE}
                            </Button>
                          </Grid>
                          <Grid size={SIZES.AUTO}>
                            <Button
                              variant={VARIANTS.CONTAINED}
                              color={BUTTON_COLORS.WARNING}
                              endIcon={<Send />}
                              onClick={() => handleSendEmail(email.emailconvid)}
                              disabled={email.cararesponseapproved === 1}
                              sx={{ minWidth: "100px" }}
                            >
                              {EMAIL_STRING.SEND_EMAIL}
                            </Button>
                          </Grid>
                        </Grid>

                        {/* RichText Editor for the CARA Response */}
                        <RichTextEditor
                          value={emailContents[email.emailconvid] || ""}
                          onChange={(newContent) =>
                            setEmailContents((prev) => ({
                              ...prev,
                              [email.emailconvid]: newContent,
                            }))
                          }
                          disabled={email.cararesponseapproved === 1}
                          sx={{
                            mr: 2,
                            backgroundColor:
                              email.cararesponseapproved === 1
                                ? "gray"
                                : undefined,
                          }}
                        />
                      </Box>
                      <Box className="ai-chatbox__message-footer">
                        <span>
                          <b>Cara</b> on{" "}
                          {new Date(email.cararesponsetime).toLocaleString(
                            "en-GB",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </Box>
                    </Box>
                    <Box
                      className="ai-chatbox__message ai-chatbox__message--input"
                      sx={{
                        borderLeftColor: theme.palette.primary.main + "60",
                      }}
                    >
                      <Box
                        className="ai-chatbox__message-content"
                        dangerouslySetInnerHTML={{ __html: email.userresponse }}
                      />
                      <Box className="ai-chatbox__message-footer">
                        <span>
                          <b>User</b> on{" "}
                          {new Date(email.userresponsetime).toLocaleString(
                            "en-GB",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}></Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
          <Typography>{EMAIL_MESSAGE.NO_EMAIL_CONVERSATION}</Typography>
        )
      ) : (
        <Typography>No conversation found.</Typography>
      )}
    </Box>
  );

  
}

export default TabEmailConversation;
